// /opt/isp_erp/frontend/src/pages/warehouse/Equipos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  CircularProgress  // ✅ Agregado
} from '@mui/material';
import { 
  Search, 
  Refresh, 
  Visibility, 
  History, 
  SwapHoriz, 
  Delete as DeleteIcon  // ✅ Agregado
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';

const Equipos = () => {
  const navigate = useNavigate();
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [tecnicoFilter, setTecnicoFilter] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [historial, setHistorial] = useState([]);

  // ✅ Estados para el diálogo de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipoToDelete, setEquipoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    cargarEquipos();
    cargarTecnicos();
  }, []);

  const cargarEquipos = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.listarEquipos();
      setEquipos(response.data || []);
    } catch (error) {
      console.error('Error cargando equipos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarTecnicos = async () => {
    try {
      const response = await api.get('/users');
      setTecnicos(response.data || []);
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  const verDetalle = async (equipo) => {
    setSelectedEquipo(equipo);
    try {
      const response = await warehouseAPI.getHistorialEquipo(equipo.sn);
      setHistorial(response.data.historial_reciente || []);
      setDetailOpen(true);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  // ✅ Funciones para eliminar
  const handleDeleteClick = (equipo) => {
    setEquipoToDelete(equipo);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await warehouseAPI.eliminarEquipo(equipoToDelete.sn);
      setDeleteSuccess(true);
      setDeleteDialogOpen(false);
      cargarEquipos(); // Recargar la lista
      
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Error al eliminar el equipo');
      setTimeout(() => setDeleteError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      BODEGA: 'success',
      EN_TRANSITO: 'warning',
      INSTALADO: 'info',
      RETIRO_PENDIENTE: 'error',
      RMA_BAJA: 'default'
    };
    return colors[estado] || 'default';
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      BODEGA: 'En Bodega',
      EN_TRANSITO: 'En Tránsito',
      INSTALADO: 'Instalado',
      RETIRO_PENDIENTE: 'Retiro Pendiente',
      RMA_BAJA: 'RMA/Baja'
    };
    return textos[estado] || estado;
  };

  const filteredEquipos = equipos.filter(equipo => {
    const matchSearch = equipo.sn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       equipo.modelo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = estadoFilter === 'todos' || equipo.estado_actual === estadoFilter;
    const matchTecnico = tecnicoFilter === '' || equipo.poseedor?.id === parseInt(tecnicoFilter);
    return matchSearch && matchEstado && matchTecnico;
  });

  const paginatedEquipos = filteredEquipos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Equipos
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por SN o Modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="BODEGA">En Bodega</MenuItem>
                  <MenuItem value="EN_TRANSITO">En Tránsito</MenuItem>
                  <MenuItem value="INSTALADO">Instalado</MenuItem>
                  <MenuItem value="RETIRO_PENDIENTE">Retiro Pendiente</MenuItem>
                  <MenuItem value="RMA_BAJA">RMA/Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Técnico</InputLabel>
                <Select
                  value={tecnicoFilter}
                  onChange={(e) => setTecnicoFilter(e.target.value)}
                  label="Técnico"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tecnicos.map((tecnico) => (
                    <MenuItem key={tecnico.id} value={tecnico.id}>
                      {tecnico.username || tecnico.nombre || tecnico.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={cargarEquipos}
                startIcon={<Refresh />}
              >
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SN</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Poseedor</TableCell>
              <TableCell>Fecha Ingreso</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : paginatedEquipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay equipos registrados
                </TableCell>
              </TableRow>
            ) : (
              paginatedEquipos.map((equipo) => (
                <TableRow key={equipo.id} hover>
                  <TableCell>
                    <strong>{equipo.sn}</strong>
                  </TableCell>
                  <TableCell>{equipo.modelo}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEstadoTexto(equipo.estado_actual)}
                      color={getEstadoColor(equipo.estado_actual)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{equipo.poseedor_nombre || 'Bodega'}</TableCell>
                  <TableCell>
                    {new Date(equipo.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => verDetalle(equipo)}
                      title="Ver detalle e historial"
                    >
                      <History />
                    </IconButton>
                    {equipo.estado_actual === 'BODEGA' && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/warehouse/despachar/${equipo.sn}`)}
                          title="Despachar a técnico"
                        >
                          <SwapHoriz />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(equipo)}
                          title="Eliminar equipo"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEquipos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Dialog de Detalle */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle del Equipo: {selectedEquipo?.sn}
        </DialogTitle>
        <DialogContent>
          {selectedEquipo && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Modelo:</Typography>
                  <Typography variant="body1">{selectedEquipo.modelo}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Estado:</Typography>
                  <Chip
                    label={getEstadoTexto(selectedEquipo.estado_actual)}
                    color={getEstadoColor(selectedEquipo.estado_actual)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Poseedor:</Typography>
                  <Typography variant="body1">
                    {selectedEquipo.poseedor_nombre || 'Bodega Central'}
                  </Typography>
                </Grid>
                {selectedEquipo.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Observaciones:</Typography>
                    <Typography variant="body1">{selectedEquipo.observaciones}</Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Historial de Movimientos
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Movimiento</TableCell>
                      <TableCell>Origen</TableCell>
                      <TableCell>Destino</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historial.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay movimientos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      historial.map((mov, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(mov.fecha_movimiento).toLocaleString()}
                          </TableCell>
                          <TableCell>{mov.tipo_movimiento}</TableCell>
                          <TableCell>{mov.origen_nombre}</TableCell>
                          <TableCell>{mov.destino_nombre}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Dialog de Confirmación de Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Esta acción es irreversible y eliminará permanentemente el equipo.
            </Alert>
            <Typography variant="body1">
              ¿Estás seguro de que deseas eliminar el siguiente equipo?
            </Typography>
            {equipoToDelete && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2">
                  <strong>SN:</strong> {equipoToDelete.sn}
                </Typography>
                <Typography variant="body2">
                  <strong>Modelo:</strong> {equipoToDelete.modelo}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {getEstadoTexto(equipoToDelete.estado_actual)}
                </Typography>
                <Typography variant="body2">
                  <strong>Fecha de ingreso:</strong> {new Date(equipoToDelete.created_at).toLocaleDateString()}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Eliminando...' : 'Eliminar Permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar de éxito */}
      {deleteSuccess && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setDeleteSuccess(false)}
        >
          Equipo eliminado correctamente
        </Alert>
      )}

      {/* ✅ Snackbar de error */}
      {deleteError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setDeleteError('')}
        >
          {deleteError}
        </Alert>
      )}
    </Box>
  );
};

export default Equipos;
